// A macro step input's arithmetic, <i+1> or <w*h>: numbers, variable names and For each refs (#j, only in the tokens
// flatten makes), + - * / %, unary minus and brackets. Parsed by hand (never eval), pure.

/** A parsed expression: a number, a variable, a For each's number (#j), -x, or two joined by an operator. */
export type Expr = { n: number } | { v: string } | { loop: number } | { neg: Expr } | { op: '+' | '-' | '*' | '/' | '%'; a: Expr; b: Expr };

const TOK = /\s*(?:(\d+(?:\.\d*)?|\.\d+)|([A-Za-z_]\w{0,39})|#(\d+)|([-+*/%()]))/y;

/** src parsed, or null when it isn't an expression (over 200 characters, a bad character, unbalanced brackets …). */
export function parseExpr(src: string): Expr | null {
  if (src.length > 200) return null;
  const toks: (Expr | string)[] = [];
  TOK.lastIndex = 0;
  while (TOK.lastIndex < src.length) {
    if (/^\s*$/.test(src.slice(TOK.lastIndex))) break;
    const m = TOK.exec(src);
    if (!m) return null;
    toks.push(m[1] !== undefined ? { n: +m[1] } : m[2] !== undefined ? { v: m[2] } : m[3] !== undefined ? { loop: +m[3] } : m[4]);
  }
  let i = 0;
  const peek = () => toks[i], is = (t: string) => peek() === t;
  const atom = (): Expr | null => {
    const t = toks[i++];
    if (t === '(') { const e = sum(); return e && toks[i++] === ')' ? e : null; }
    if (t === '-') { const e = atom(); return e && { neg: e }; }
    return t !== undefined && typeof t !== 'string' ? t : null;
  };
  const prod = (): Expr | null => {
    let a = atom();
    while (a && (is('*') || is('/') || is('%'))) { const op = toks[i++] as '*'; const b = atom(); a = b && { op, a, b }; }
    return a;
  };
  const sum = (): Expr | null => {
    let a = prod();
    while (a && (is('+') || is('-'))) { const op = toks[i++] as '+'; const b = prod(); a = b && { op, a, b }; }
    return a;
  };
  const e = sum();
  return e && i === toks.length ? e : null;
}

/** The variable names in e, each once, in the order they come. */
export function exprNames(e: Expr, out: string[] = []): string[] {
  if ('v' in e) { if (!out.includes(e.v)) out.push(e.v); } else if ('neg' in e) exprNames(e.neg, out); else if ('op' in e) { exprNames(e.a, out); exprNames(e.b, out); }
  return out;
}

/** The For each refs (#j) in e. */
export function exprLoops(e: Expr, out: number[] = []): number[] {
  if ('loop' in e) out.push(e.loop); else if ('neg' in e) exprLoops(e.neg, out); else if ('op' in e) { exprLoops(e.a, out); exprLoops(e.b, out); }
  return out;
}

/** e worked out, look giving each variable's and For each ref's number; throws when it comes to no number (÷ 0). */
export function evalExpr(e: Expr, look: (name: string | number) => number): number {
  const go = (e: Expr): number => {
    if ('n' in e) return e.n;
    if ('v' in e) return look(e.v);
    if ('loop' in e) return look(e.loop);
    if ('neg' in e) return -go(e.neg);
    const a = go(e.a), b = go(e.b);
    return e.op === '+' ? a + b : e.op === '-' ? a - b : e.op === '*' ? a * b : e.op === '/' ? a / b : a % b;
  };
  const x = go(e);
  if (!Number.isFinite(x)) throw new Error('comes to no number (divided by 0?)');
  return x;
}

/** e written out again, sub giving what a variable becomes (an Expr: a number, a For each ref …; undefined: kept). */
export function exprText(e: Expr, sub: (name: string) => Expr | undefined = () => undefined): string {
  const go = (e: Expr): string => {
    if ('n' in e) return e.n < 0 ? `(${e.n})` : String(e.n);
    if ('v' in e) { const s = sub(e.v); return s ? go(s) : e.v; }
    if ('loop' in e) return `#${e.loop}`;
    if ('neg' in e) return `-(${go(e.neg)})`;
    return `(${go(e.a)}${e.op}${go(e.b)})`;
  };
  return go(e);
}
